import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { BarChart as BarChartIcon, TableRows as TableIcon } from '@mui/icons-material';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartDownloadImageButton } from '../ChartDownloadImageButton';
import {
  formatInt,
  formatPercent,
  METRIC_LABEL,
  ratio,
  ShareMetric,
  ShareVizPalette,
  TrendBucket,
  TrendView,
  VIEW_UNIT,
} from './shareLinksAnalyticsModel';
import { shareCardSx, ShareTooltipShell, ShareTooltipRow } from './shareLinksUi';

const CHART_HEIGHT = 280;

interface TrendTooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: TrendBucket }>;
  metric: ShareMetric;
  view: TrendView;
  palette: ShareVizPalette;
}

const TrendTooltip: React.FC<TrendTooltipProps> = ({ active, payload, metric, view, palette }) => {
  const bucket = payload?.[0]?.payload;
  if (!active || !bucket) return null;
  const cumulative = view === 'cumulative';
  return (
    <ShareTooltipShell title={cumulative ? `Up to ${bucket.title}` : bucket.title}>
      <ShareTooltipRow color={palette[metric]} label={cumulative ? `${METRIC_LABEL[metric]} so far` : METRIC_LABEL[metric]} value={formatInt(bucket[metric])} strong />
      {metric === 'links' && bucket.sharers !== null ? (
        <ShareTooltipRow label="Agents sharing" value={formatInt(bucket.sharers)} />
      ) : null}
      {metric === 'leads' ? (
        <>
          <ShareTooltipRow label="Started WhatsApp verification" value={formatInt(bucket.verificationsStarted)} />
          <ShareTooltipRow
            label="Didn't finish"
            value={`${formatInt(bucket.verificationsAbandoned)} (${formatPercent(ratio(bucket.verificationsAbandoned, bucket.verificationsStarted))})`}
          />
        </>
      ) : null}
    </ShareTooltipShell>
  );
};

/** Value label drawn on the tallest bar only — one direct label, not one per bar. */
function peakLabel(peakIndex: number, color: string) {
  return (props: { x?: number | string; y?: number | string; width?: number | string; value?: unknown; index?: number }) => {
    if (props.index !== peakIndex) return null;
    const x = Number(props.x) + Number(props.width) / 2;
    const y = Number(props.y) - 6;
    return (
      <text x={x} y={y} textAnchor="middle" fontSize={11} fontWeight={600} fill={color}>
        {formatInt(Number(props.value) || 0)}
      </text>
    );
  };
}

interface ShareLinksTrendCardProps {
  metric: ShareMetric;
  title: string;
  buckets: TrendBucket[];
  view: TrendView;
  palette: ShareVizPalette;
  tickColor: string;
  inkColor: string;
  fileName: string;
}

const ShareLinksTrendCard: React.FC<ShareLinksTrendCardProps> = ({
  metric,
  title,
  buckets,
  view,
  palette,
  tickColor,
  inkColor,
  fileName,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [showTable, setShowTable] = useState(false);
  const color = palette[metric];
  const cumulative = view === 'cumulative';
  const unit = VIEW_UNIT[view];

  const stats = useMemo(() => {
    if (buckets.length === 0) return null;
    if (cumulative) {
      const last = buckets[buckets.length - 1];
      const prior = buckets.length > 1 ? buckets[buckets.length - 2][metric] : 0;
      return { kind: 'cumulative' as const, total: last[metric], latestGain: last[metric] - prior };
    }
    let peakIndex = 0;
    let total = 0;
    buckets.forEach((b, i) => {
      total += b[metric];
      if (b[metric] > buckets[peakIndex][metric]) peakIndex = i;
    });
    return {
      kind: 'periodic' as const,
      total,
      peakIndex,
      peak: buckets[peakIndex],
      average: total / buckets.length,
    };
  }, [buckets, cumulative, metric]);

  const isEmpty = !stats || stats.total === 0;
  const tick = { fill: tickColor, fontSize: 11 };

  const headline = !stats || isEmpty
    ? 'Nothing in this period.'
    : stats.kind === 'cumulative'
      ? `${formatInt(stats.total)} in total · +${formatInt(stats.latestGain)} on the last day`
      : `Peak ${formatInt(stats.peak[metric])} (${stats.peak.title}) · avg ${stats.average.toFixed(stats.average >= 10 ? 0 : 1)} per ${unit}`;

  return (
    <Box ref={cardRef} sx={shareCardSx}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{title}</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.62)' }}>{headline}</Typography>
        </Box>
        <Box data-html2canvas-ignore="true" sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Tooltip title={showTable ? 'Show chart' : 'Show as table'}>
            <IconButton size="small" onClick={() => setShowTable((v) => !v)} aria-label={showTable ? 'Show chart' : 'Show as table'}>
              {showTable ? <BarChartIcon fontSize="small" /> : <TableIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <ChartDownloadImageButton targetRef={cardRef} fileName={fileName} />
        </Box>
      </Box>

      {showTable ? (
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: CHART_HEIGHT, bgcolor: 'transparent' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: 'var(--color-surface)' }}>{cumulative ? 'Up to' : 'Period'}</TableCell>
                <TableCell align="right" sx={{ bgcolor: 'var(--color-surface)' }}>{METRIC_LABEL[metric]}</TableCell>
                {metric === 'links' && view === 'daily' ? (
                  <TableCell align="right" sx={{ bgcolor: 'var(--color-surface)' }}>Agents sharing</TableCell>
                ) : null}
                {metric === 'leads' ? (
                  <>
                    <TableCell align="right" sx={{ bgcolor: 'var(--color-surface)' }}>Verifications started</TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'var(--color-surface)' }}>Didn&apos;t finish</TableCell>
                  </>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...buckets].reverse().map((b) => (
                <TableRow key={b.key} hover>
                  <TableCell>{b.title}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatInt(b[metric])}</TableCell>
                  {metric === 'links' && view === 'daily' ? (
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatInt(b.sharers ?? 0)}</TableCell>
                  ) : null}
                  {metric === 'leads' ? (
                    <>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatInt(b.verificationsStarted)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatInt(b.verificationsAbandoned)}</TableCell>
                    </>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : isEmpty ? (
        <Box sx={{ height: CHART_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
            No {METRIC_LABEL[metric].toLowerCase()} in this period.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%', height: CHART_HEIGHT, minWidth: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            {cumulative ? (
              <AreaChart data={buckets} margin={{ top: 16, right: 12, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id={`share-trend-${metric}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.22} />
                    <stop offset="100%" stopColor={color} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={palette.grid} vertical={false} />
                <XAxis dataKey="label" tick={tick} stroke={palette.axis} tickLine={false} minTickGap={18} />
                <YAxis allowDecimals={false} tick={tick} stroke="none" width={44} />
                <RechartsTooltip
                  content={<TrendTooltip metric={metric} view={view} palette={palette} />}
                  cursor={{ stroke: palette.axis, strokeWidth: 1 }}
                />
                <Area
                  type="monotone"
                  dataKey={metric}
                  name={METRIC_LABEL[metric]}
                  stroke={color}
                  strokeWidth={2}
                  fill={`url(#share-trend-${metric})`}
                  dot={false}
                  activeDot={{ r: 5, stroke: 'var(--color-surface)', strokeWidth: 2, fill: color }}
                  isAnimationActive={false}
                />
              </AreaChart>
            ) : (
              <BarChart data={buckets} margin={{ top: 20, right: 40, left: -8, bottom: 0 }}>
                <CartesianGrid stroke={palette.grid} vertical={false} />
                <XAxis dataKey="label" tick={tick} stroke={palette.axis} tickLine={false} minTickGap={18} />
                <YAxis allowDecimals={false} tick={tick} stroke="none" width={44} />
                <RechartsTooltip
                  content={<TrendTooltip metric={metric} view={view} palette={palette} />}
                  cursor={{ fill: palette.selection }}
                />
                {stats && stats.kind === 'periodic' && buckets.length > 2 ? (
                  <ReferenceLine
                    y={stats.average}
                    stroke={palette.axis}
                    strokeWidth={1}
                    label={{
                      value: `avg ${stats.average.toFixed(stats.average >= 10 ? 0 : 1)}`,
                      position: 'right',
                      fill: tickColor,
                      fontSize: 10,
                    }}
                  />
                ) : null}
                <Bar
                  dataKey={metric}
                  name={METRIC_LABEL[metric]}
                  fill={color}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={24}
                  isAnimationActive={false}
                >
                  {stats && stats.kind === 'periodic' ? (
                    <LabelList dataKey={metric} content={peakLabel(stats.peakIndex, inkColor)} />
                  ) : null}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

export default ShareLinksTrendCard;
